import {
  createContext, useCallback, useContext, useEffect, useMemo,
  useRef, useState, type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Box, Button, Stack, Typography, alpha } from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import StopIcon              from '@mui/icons-material/Stop';
import VideocamIcon          from '@mui/icons-material/Videocam';
import DeleteOutlineIcon     from '@mui/icons-material/DeleteOutline';
import BugReportIcon         from '@mui/icons-material/BugReport';

const MAX_SECONDS = 120;

function fmtTime(s: number) {
  const m   = Math.floor(s / 60).toString().padStart(2, '0');
  const sec = (s % 60).toString().padStart(2, '0');
  return `${m}:${sec}`;
}

export type RecordingPhase = 'idle' | 'recording' | 'preview';

interface RecordingCtx {
  phase:          RecordingPhase;
  seconds:        number;
  blob:           Blob | null;
  startRecording: () => Promise<void>;
  stopRecording:  () => void;
  discard:        () => void;
  consumeBlob:    () => Blob | null;
}

const Ctx = createContext<RecordingCtx | null>(null);

export function useRecording(): RecordingCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useRecording must be used within RecordingProvider');
  return ctx;
}

export function RecordingProvider({ children }: { children: ReactNode }) {
  const [phase,   setPhase]   = useState<RecordingPhase>('idle');
  const [seconds, setSeconds] = useState(0);
  const [blob,    setBlob]    = useState<Blob | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef   = useRef<BlobPart[]>([]);
  const timerRef    = useRef<ReturnType<typeof setInterval> | null>(null);
  const streamRef   = useRef<MediaStream | null>(null);
  const blobRef     = useRef<Blob | null>(null);

  // Cleanup on unmount
  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
  }, []);

  const stopRecording = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
  }, []);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
      streamRef.current = stream;
      chunksRef.current = [];

      const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
        ? 'video/webm;codecs=vp9'
        : 'video/webm';
      const recorder = new MediaRecorder(stream, { mimeType });
      recorderRef.current = recorder;

      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = () => {
        const recorded = new Blob(chunksRef.current, { type: mimeType });
        blobRef.current = recorded;
        setBlob(recorded);
        setPhase('preview');
        stream.getTracks().forEach((t) => t.stop());
      };

      stream.getVideoTracks()[0].onended = () => {
        if (recorder.state === 'recording') stopRecording();
      };

      recorder.start(250);
      setPhase('recording');
      setSeconds(0);
      timerRef.current = setInterval(() => {
        setSeconds((s) => {
          if (s + 1 >= MAX_SECONDS) { stopRecording(); return MAX_SECONDS; }
          return s + 1;
        });
      }, 1000);
    } catch {
      // User cancelled getDisplayMedia
    }
  }, [stopRecording]);

  const discard = useCallback(() => {
    blobRef.current = null;
    setBlob(null);
    setPhase('idle');
    setSeconds(0);
  }, []);

  const consumeBlob = useCallback((): Blob | null => {
    const b = blobRef.current;
    blobRef.current = null;
    setBlob(null);
    setPhase('idle');
    setSeconds(0);
    return b;
  }, []);

  const value = useMemo<RecordingCtx>(() => ({
    phase, seconds, blob, startRecording, stopRecording, discard, consumeBlob,
  }), [phase, seconds, blob, startRecording, stopRecording, discard, consumeBlob]);

  return (
    <Ctx.Provider value={value}>
      {children}
      <RecordingPill phase={phase} seconds={seconds} onStop={stopRecording} onDiscard={discard} onAttach={consumeBlob} />
    </Ctx.Provider>
  );
}

// ─── Floating pill ────────────────────────────────────────────────────────────

function RecordingPill({
  phase, seconds, onStop, onDiscard, onAttach,
}: {
  phase:     RecordingPhase;
  seconds:   number;
  onStop:    () => void;
  onDiscard: () => void;
  onAttach:  () => Blob | null;
}) {
  if (phase === 'idle') return null;

  const openReport = () => {
    const blob = onAttach();
    window.dispatchEvent(new CustomEvent<{ blob: Blob | null }>('bugreport:open', { detail: { blob } }));
  };

  const pillBase = {
    position: 'fixed' as const,
    bottom: 24,
    right: 24,
    zIndex: 9999,
    bgcolor: '#1A1A1A',
    borderRadius: 6,
    px: 2,
    py: 1,
    boxShadow: '0 4px 20px rgba(0,0,0,0.6)',
    backdropFilter: 'blur(8px)',
  };

  if (phase === 'recording') {
    return createPortal(
      <Stack direction="row" alignItems="center" spacing={1.5}
        sx={{ ...pillBase, border: `1px solid ${alpha('#EF4444', 0.45)}` }}
      >
        <FiberManualRecordIcon sx={{
          color: '#EF4444', fontSize: 13,
          '@keyframes recPulse': {
            '0%, 100%': { opacity: 1 },
            '50%':       { opacity: 0.25 },
          },
          animation: 'recPulse 1s ease-in-out infinite',
        }} />
        <Typography variant="body2" fontWeight={700} color="#EF4444" sx={{ lineHeight: 1 }}>
          Grabando
        </Typography>
        <Typography variant="body2" fontWeight={700}
          sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.primary', lineHeight: 1 }}>
          {fmtTime(seconds)}
        </Typography>
        <Button
          size="small" variant="outlined"
          startIcon={<StopIcon sx={{ fontSize: '14px !important' }} />}
          onClick={onStop}
          sx={{
            textTransform: 'none',
            borderColor: alpha('#EF4444', 0.5),
            color: '#EF4444',
            py: 0.25, px: 1, minWidth: 0, fontSize: 12, lineHeight: 1.4,
          }}
        >
          Detener
        </Button>
      </Stack>,
      document.body,
    );
  }

  // preview
  return createPortal(
    <Stack direction="row" alignItems="center" spacing={1.5}
      sx={{ ...pillBase, border: `1px solid ${alpha('#60A5FA', 0.45)}` }}
    >
      <VideocamIcon sx={{ color: '#60A5FA', fontSize: 16, flexShrink: 0 }} />
      <Typography variant="body2" fontWeight={700} color="#60A5FA" sx={{ lineHeight: 1, flexShrink: 0 }}>
        Video listo · {fmtTime(seconds)}
      </Typography>
      <Box sx={{ width: '1px', height: 20, bgcolor: 'rgba(255,255,255,0.08)', flexShrink: 0 }} />
      <Button
        size="small"
        startIcon={<DeleteOutlineIcon sx={{ fontSize: '14px !important' }} />}
        onClick={onDiscard}
        sx={{
          textTransform: 'none', color: 'text.secondary',
          py: 0.25, px: 1, minWidth: 0, fontSize: 12, lineHeight: 1.4,
        }}
      >
        Descartar
      </Button>
      <Button
        size="small" variant="contained"
        startIcon={<BugReportIcon sx={{ fontSize: '14px !important' }} />}
        onClick={openReport}
        sx={{
          textTransform: 'none', bgcolor: '#60A5FA', color: '#0A0A0A',
          fontWeight: 700, py: 0.25, px: 1.25, minWidth: 0, fontSize: 12, lineHeight: 1.4,
          '&:hover': { bgcolor: '#93C5FD' },
        }}
      >
        Adjuntar al reporte
      </Button>
    </Stack>,
    document.body,
  );
}
